/* eslint-disable react-hooks/exhaustive-deps */
import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import Pagination from "./Pagination";
import { useGallery } from "../hooks/useImage";

export default function Gallery() {
  const { t } = useTranslation();
  
  const excludedIds = [
    "riB8qGHUbkOY2UgoiolS", 
    "qLKZaB8y8BHcJAwIh9G7", 
    "DUhixZYzXwFzwAS2qAVT", 
    "21YEk5eklUB69Hz7GNUr", 
    "9LnlAa5zRLHaKRo3OzYf", 
    "BMNpcuGh53IAqgQBSyAi", 
    "MXpe2narye9Pgv6nzxW9"
  ];

  const { images, loading, error } = useGallery(excludedIds);

  const [isOpen, setIsOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [imagesPerPage] = useState(6);
  const [animationKey, setAnimationKey] = useState(0);

  const totalPages = Math.ceil(images.length / imagesPerPage);
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);
  const indexOfLastImage = currentPage * imagesPerPage;
  const indexOfFirstImage = indexOfLastImage - imagesPerPage;
  const currentImages = images.slice(indexOfFirstImage, indexOfLastImage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  function openModal(image) {
    setSelectedImage(image);
    setIsOpen(true);
  }

  function closeModal() {
    setSelectedImage(null);
    setIsOpen(false);
  }

  useEffect(() => {
    setAnimationKey(prev => prev + 1);
  }, [currentPage]);

  if (loading) return <div className="bg-custom-rose min-h-screen flex items-center justify-center text-white font-bold">Loading Gallery...</div>;
  if (error) return <div className="bg-custom-rose min-h-screen flex items-center justify-center text-red-200">Error: {error}</div>;

  return (
    <div className="bg-custom-rose min-h-screen">
      <motion.div 
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
        <div className="py-6 sm:pt-12 px-6">
          <div className="mx-auto max-w-7xl">
            <p className="text-base font-semibold leading-7 text-gray-200">
              {t("galleryHeading")}
            </p>
            <h2 className="mt-2 text-4xl font-bold tracking-tight text-white sm:text-6xl">
              {t("gallery")}
            </h2>
            <p className="mt-6 text-lg leading-8 text-white">
              {t("galleryDescription")}
            </p>
          </div>
        </div>

        <section className="overflow-hidden">
          <div className="container mx-auto px-5 py-2 lg:px-32 lg:py-12">
            <motion.div
              key={animationKey}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {currentImages.map((image) => (
                <div key={image.id} className="relative group">
                  <div className="aspect-w-1 aspect-h-1 w-full overflow-hidden rounded-xl bg-gray-800 shadow-2xl">
                    <img
                      className="w-full h-full object-cover cursor-pointer transition-all duration-500 group-hover:scale-110"
                      src={image.imageUrl}
                      alt="Gallery"
                      onClick={() => openModal(image)}
                    />
                  </div>
                </div>
              ))}
            </motion.div>
            
            {images.length > imagesPerPage && (
              <div className="mt-16 flex justify-center">
                <Pagination
                  totalPages={totalPages}
                  currentPage={currentPage}
                  pageNumbers={pageNumbers}
                  onPageChange={handlePageChange}
                />
              </div>
            )}

            <Transition appear show={isOpen} as={Fragment}>
              <Dialog as="div" className="fixed inset-0 z-[100] overflow-y-auto" onClose={closeModal}>
                <div className="flex min-h-screen items-center justify-center p-4 text-center">
                  <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                  >
                    <Dialog.Overlay className="fixed inset-0 bg-black/90" />
                  </Transition.Child>

                  {/* Modal Container */}
                  {selectedImage && (
                    <Transition.Child
                      as={Fragment}
                      enter="ease-out duration-300"
                      enterFrom="opacity-0 scale-95"
                      enterTo="opacity-100 scale-100"
                      leave="ease-in duration-200"
                    >
                      {/* FIX: 
                        - Changed max-w-5xl to max-w-2xl
                        - Added w-full and h-auto
                        - Added max-h-[85vh] to keep image contained within the screen height
                      */}
                      <div className="relative inline-block w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-1 text-left align-middle shadow-2xl transition-all">
                        <img
                          className="w-full h-auto max-h-[85vh] rounded-xl object-contain"
                          src={selectedImage.imageUrl}
                          alt="Gallery Expanded"
                        />
                        {/* Added a close button for better UX */}
                        <button
                          onClick={closeModal}
                          className="absolute top-4 right-4 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </Transition.Child>
                  )}
                </div>
              </Dialog>
            </Transition>
          </div>
        </section>
      </motion.div>
    </div>
  );
}